import { CircleDollarSign, Crosshair, Shield, Swords, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export type UnifiedMatchPlayer = {
  id: string;
  name: string;
  team: string;
  agent: string;
  agentIcon?: string;
  rank?: string | null;
  tracked?: boolean;
  kills: number;
  deaths: number;
  assists: number;
  acs: number;
  adr: number;
  headshots: number;
  bodyshots: number;
  legshots: number;
  damageMade: number;
  damageReceived: number;
  economySpent: number;
  economyLoadoutValue: number;
  abilityCasts: { c: number; q: number; e: number; x: number };
};

export const UnifiedMatchOverview = ({
  players,
  focusPlayerId,
  ownTeam,
  rounds,
}: {
  players: UnifiedMatchPlayer[];
  focusPlayerId: string;
  ownTeam: string;
  rounds: number;
}) => {
  const focus = players.find((player) => player.id === focusPlayerId) ?? players[0];
  const teams = Array.from(new Set(players.map((player) => player.team)));
  if (!focus) return null;
  const impacts = focus.headshots + focus.bodyshots + focus.legshots;

  return (
    <div className="space-y-5">
      <section className="grid gap-4 xl:grid-cols-4">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Crosshair className="h-4 w-4 text-primary" />
              Precisión de {focus.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 divide-x divide-border rounded-lg border border-border bg-muted/20">
              {[
                ["Cabeza", focus.headshots, "text-amber-600"],
                ["Cuerpo", focus.bodyshots, "text-foreground"],
                ["Piernas", focus.legshots, "text-muted-foreground"],
              ].map(([label, value, color]) => (
                <div key={label} className="p-3 text-center">
                  <p className={`text-xl font-semibold tabular-nums ${color}`}>{value}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>{impacts} impactos registrados</span>
              <span>{Math.round((focus.headshots / Math.max(1, impacts)) * 100)}% headshot</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CircleDollarSign className="h-4 w-4 text-primary" /> Economía
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Créditos gastados" value={focus.economySpent.toLocaleString("es-CL")} />
            <Row label="Valor de loadout" value={focus.economyLoadoutValue.toLocaleString("es-CL")} />
            <div className="border-t border-border pt-3">
              <Row label="Daño / ronda" value={Math.round(focus.damageMade / Math.max(1, rounds))} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-4 w-4 text-primary" /> Habilidades
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-4 gap-2">
            {(["c", "q", "e", "x"] as const).map((key) => (
              <div key={key} className="rounded-md border border-border bg-muted/20 p-2 text-center">
                <p className="text-lg font-semibold tabular-nums">{focus.abilityCasts[key]}</p>
                <p className="text-[10px] font-medium uppercase text-muted-foreground">{key}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
      {teams.map((team) => (
        <Card key={team}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {team === ownTeam ? <Shield className="h-5 w-5 text-primary" /> : <Swords className="h-5 w-5 text-muted-foreground" />}
              {team === ownTeam ? "Equipo aliado" : "Equipo rival"}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="pb-3 text-left font-medium">Jugador</th><th className="pb-3 text-left font-medium">Agente</th><th className="pb-3 text-right font-medium">K / D / A</th><th className="pb-3 text-right font-medium">ACS</th><th className="pb-3 text-right font-medium">ADR</th><th className="pb-3 text-right font-medium">HS</th><th className="pb-3 text-right font-medium">Daño +/-</th>
              </tr></thead>
              <tbody>{players.filter((player) => player.team === team).sort((a, b) => b.acs - a.acs).map((player) => {
                const shots = player.headshots + player.bodyshots + player.legshots;
                return <tr key={player.id} className="border-b border-border/60 last:border-0">
                  <td className="py-3"><span className="font-semibold">{player.name}</span>{player.tracked && <Badge className="ml-2" variant="outline">Roster</Badge>}</td>
                  <td className="py-3"><div className="flex items-center gap-2">{player.agentIcon ? <span className="h-7 w-7 rounded-md border border-border bg-muted bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${player.agentIcon})` }} /> : <span className="h-7 w-7 rounded-md border border-border bg-muted" />}{player.agent}</div></td>
                  <td className="py-3 text-right font-mono">{player.kills}/{player.deaths}/{player.assists}</td><td className="py-3 text-right tabular-nums">{player.acs}</td><td className="py-3 text-right tabular-nums">{player.adr}</td><td className="py-3 text-right tabular-nums">{Math.round((player.headshots / Math.max(1, shots)) * 100)}%</td><td className="py-3 text-right tabular-nums">{player.damageMade - player.damageReceived}</td>
                </tr>;
              })}</tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{label}</span><span className="font-semibold tabular-nums">{value}</span></div>
);
